// This is duplicated from pi-client-admin and once pi-tapestry
// has a functional date-picker this can be removed
import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    forwardRef
} from '@angular/core';
import {
    AbstractControl,
    AbstractControlDirective,
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    OWL_DATE_TIME_FORMATS,
    OwlDateTimeInputDirective
} from '@danielmoncada/angular-datetime-picker';
import {
    piIconCalendar,
    piIconClosePage
} from '@predictiveindex/pi-client-icons';
import {
    TapestryIconButtonModule,
    TapestryIconModule,
    TapestryIconRegistryService
} from '@predictiveindex/tapestry';
import { DirectivesModule } from '../core-ui';
import { TranslationService } from '../translations';

export const DATE_TIME_PICKER_FORMATS = {
    fullPickerInput: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
    },
    datePickerInput: { year: 'numeric', month: 'short', day: 'numeric' },
    timePickerInput: { hour: 'numeric', minute: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
};

export type DatePickerChangeEvent = {
    source: OwlDateTimeInputDirective<string>;
    input: HTMLInputElement;
    value: string;
};

@Component({
    selector: `date-picker`,
    imports: [
        CommonModule,
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        DirectivesModule,
        FormsModule,
        TapestryIconButtonModule,
        TapestryIconModule
    ],
    providers: [
        { provide: OWL_DATE_TIME_FORMATS, useValue: DATE_TIME_PICKER_FORMATS },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        }
    ],
    templateUrl: `./date-picker.component.html`,
    styleUrls: [`./date-picker.component.less`]
})
// Owl Date Time Picker is used until Date/Time picker component is fixed in tapesty
// https://daniel-projects.firebaseapp.com/owlng/date-time-picker
export class DatePickerComponent implements OnInit, ControlValueAccessor {
    /**
     * The date/time picker label
     */
    @Input() labelText = '';
    /**
     * The initial value set for the date/time picker
     */
    @Input() value?: Date;
    /**
     * The min date available for selection in the date/time picker
     */
    @Input() minDate: Date = new Date();
    /**
     * The max date available for selection in the date/time picker
     */
    @Input() maxDate: Date = new Date();

    @Input() testingAttribute = '';

    @Input() isOptional = false;

    /**
     * Form control input
     */
    @Input() control?: AbstractControl | AbstractControlDirective;

    @Output() updateDates = new EventEmitter<DatePickerChangeEvent>();

    optionalLabel: string;
    rightMarginCalendar = '30px';
    disabled = false;

    constructor(
        private readonly TR: TranslationService,
        private readonly tapestryIconRegistry: TapestryIconRegistryService
    ) {}

    ngOnInit() {
        this.tapestryIconRegistry.registerIcons([
            piIconClosePage,
            piIconCalendar
        ]);
        this.optionalLabel = this.getOptionalLabel();
        this.rightMarginCalendar = this.getMarginRight();
    }

    onChange = (_: string): void => {};

    onTouched = (): void => {};

    handleUpdateDates(event: DatePickerChangeEvent): void {
        this.rightMarginCalendar = this.getMarginRight();
        this.updateDates.emit(event);
    }

    clearDate(): void {
        this.value = undefined;
        this.rightMarginCalendar = this.getMarginRight();
        this.updateDates.emit();
    }

    writeValue(newValue: Date): void {
        this.value = newValue;
    }

    registerOnChange(onChange: (value: string) => void): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private getOptionalLabel(): string {
        return this.TR.key('label.common-terms.optional').toLowerCase();
    }

    private getMarginRight(): string {
        // adjust margin of calendar icon based
        // on the existance of cancel button
        return this.value ? '30px' : '5px';
    }
}
